import {useEffect} from 'react';

import {GetAnalysis, GetAnalysisVariables} from 'analysis-tool/graphql/__generated/GetAnalysis';
import {
  GetAnalysisModifiedAt,
  GetAnalysisModifiedAtVariables,
} from 'analysis-tool/graphql/__generated/GetAnalysisModifiedAt';
import {GET_ANALYSIS_QUERY, GET_ANALYSIS_MODIFIED_AT_QUERY} from 'analysis-tool/graphql/GetAnalysis';
import {BenchlingQueryResult, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

const ANALYSIS_POLL_INTERVAL_MS = 500;

interface ModifiedAtInformation {
  modifiedAt: string | null;
  previousModifiedAt: string | null;
}
const modifiedAtGlobalState = makeUseGlobalClientState<ModifiedAtInformation>({
  modifiedAt: null,
  previousModifiedAt: null,
});

/**
 * Hook to poll for the input analysis's `modifiedAt`. Returns both the previous value of `modifiedAt` and the
 * new value after polling.
 */
export function usePollingModifiedAt(analysisId: string): ModifiedAtInformation {
  const [modifiedAtInformation, setModifiedAtInformation] =
    modifiedAtGlobalState.useGlobalClientState(analysisId);
  const queryResult = useBenchlingQuery<GetAnalysisModifiedAt, GetAnalysisModifiedAtVariables>(
    GET_ANALYSIS_MODIFIED_AT_QUERY,
    {
      variables: {analysisId},
      pollInterval: ANALYSIS_POLL_INTERVAL_MS,
      // Without this, onCompleted doesn't seem to be called each poll cycle for... some reason.
      renderCachedDataWhenRefetching: true,
      onCompleted: (result) => {
        const currentModifiedAt = modifiedAtInformation.modifiedAt;
        const newModifiedAt = isInaccessible(result.analysis) ? null : result.analysis.modifiedAt;

        if (newModifiedAt !== currentModifiedAt) {
          setModifiedAtInformation({
            modifiedAt: newModifiedAt,
            previousModifiedAt: currentModifiedAt,
          });
        }
      },
    }
  );

  // limit polling to only when the Benchling tab is visible
  // temporarily stop polling when the tab is hidden
  useEffect(() => {
    const onVisibilityChange = (): void => {
      if (document.hidden) {
        queryResult.stopPolling();
      } else {
        queryResult.startPolling(ANALYSIS_POLL_INTERVAL_MS);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [queryResult]);

  return modifiedAtInformation;
}

/**
 * Hook to poll for an Analysis.
 */
export default function usePollingAnalysis(
  analysisId: string
): BenchlingQueryResult<GetAnalysis, GetAnalysisVariables> {
  const {modifiedAt, previousModifiedAt} = usePollingModifiedAt(analysisId);

  const result = useBenchlingQuery<GetAnalysis, GetAnalysisVariables>(GET_ANALYSIS_QUERY, {
    variables: {analysisId},
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: true,
  });

  useEffect(() => {
    // Don't refetch if `previousModifiedAt` is null because a change between the previous and current value
    // is not meaningful
    if (previousModifiedAt != null && previousModifiedAt !== modifiedAt) {
      result.refetch();
    }
    // We don't want to refetch when `result` changes, so it's explicitly not in the dependency array
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [previousModifiedAt, modifiedAt]);

  return result;
}
